import { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import "./Contact.css"

const Contact = () => {
    const form = useRef();
    const [formData, setFormData] = useState({ name: '', email: '', message: '' });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const validateField = (name, value) => {
        switch (name) {
            case 'name':
                return value.trim().length < 2 ? 'Name must be at least 2 characters' : '';
            case 'email':
                return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Please enter a valid email' : '';
            case 'message':
                return value.trim().length < 10 ? 'Message must be at least 10 characters' : '';
            default:
                return '';
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        const error = validateField(name, value);
        setErrors(prev => ({ ...prev, [name]: error }));
    };

    const sendEmail = (e) => {
        e.preventDefault();
        
        const newErrors = {
            name: validateField('name', formData.name),
            email: validateField('email', formData.email),
            message: validateField('message', formData.message)
        };
        
        setErrors(newErrors);
        
        if (Object.values(newErrors).some(error => error)) {
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);

        emailjs.sendForm(
            'service_3zg6cfi',
            'template_2czi34k',
            form.current,
            'oozS_iKHxn2RXNKAh'
        )
        .then(() => {
            setSubmitStatus('success');
            setFormData({ name: '', email: '', message: '' });
            e.target.reset();
        })
        .catch(() => {
            setSubmitStatus('error');
        })
        .finally(() => {
            setIsSubmitting(false);
            setTimeout(() => setSubmitStatus(null), 5000);
        });
    };
    
    return (
        <section id='contact' className="contact section">
            <div className="contact__header">
                <h2 className="section__title">Get In Touch</h2>
                <p className="section__subtitle">Let's work together on your next project</p>
                <div className="contact__divider"></div>
            </div>

            <div className="contact__container container">
                <div className="contact__info-section">
                    <h3 className="contact__info-title">Let's Connect</h3>
                    <p className="contact__info-text">Feel free to reach out through any of these channels</p>
                    
                    <div className="contact__cards">
                        <a href="mailto:mootazhalleb@gmail.com" className="contact__card">
                            <div className="contact__card-icon-wrapper">
                                <i className="bx bx-envelope"></i>
                            </div>
                            <div className="contact__card-content">
                                <h4>Email</h4>
                                <p>mootazhalleb@gmail.com</p>
                            </div>
                            <i className="bx bx-right-arrow-alt contact__card-arrow"></i>
                        </a>

                        <a href="https://wa.me/21654449159?text=Hello%20there!" target="_blank" rel="noopener noreferrer" className="contact__card">
                            <div className="contact__card-icon-wrapper">
                                <i className="bx bxl-whatsapp"></i>
                            </div>
                            <div className="contact__card-content">
                                <h4>WhatsApp</h4>
                                <p>+216 54449159</p>
                            </div>
                            <i className="bx bx-right-arrow-alt contact__card-arrow"></i>
                        </a>

                        <a href="https://m.me/motezhalleb" target="_blank" rel="noopener noreferrer" className="contact__card">
                            <div className="contact__card-icon-wrapper">
                                <i className="bx bxl-messenger"></i>
                            </div>
                            <div className="contact__card-content">
                                <h4>Messenger</h4>
                                <p>Motez Halleb</p>
                            </div>
                            <i className="bx bx-right-arrow-alt contact__card-arrow"></i>
                        </a>
                    </div>
                </div>

                <div className="contact__form-section">
                    <h3 className="contact__form-title">Send Me a Message</h3>
                    <p className="contact__form-text">I'll get back to you as soon as possible</p>

                    <form className="contact__form" ref={form} onSubmit={sendEmail}>
                        <div className="form__group">
                            <div className="form__field">
                                <label className="form__label">Your Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`form__input ${errors.name ? 'error' : formData.name ? 'valid' : ''}`}
                                    placeholder="John Doe"
                                />
                                {errors.name && <span className="form__error">{errors.name}</span>}
                            </div>

                            <div className="form__field">
                                <label className="form__label">Your Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={`form__input ${errors.email ? 'error' : formData.email ? 'valid' : ''}`}
                                    placeholder="john@example.com"
                                />
                                {errors.email && <span className="form__error">{errors.email}</span>}
                            </div>
                        </div>

                        <div className="form__field">
                            <label className="form__label">Your Message</label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                className={`form__textarea ${errors.message ? 'error' : formData.message ? 'valid' : ''}`}
                                placeholder="Tell me about your project..."
                                rows="6"
                            ></textarea>
                            {errors.message && <span className="form__error">{errors.message}</span>}
                        </div>

                        <button type="submit" className="form__submit" disabled={isSubmitting}>
                            {isSubmitting ? (
                                <>
                                    <i className="bx bx-loader-alt bx-spin"></i>
                                    Sending...
                                </>
                            ) : (
                                <>
                                    <i className="bx bx-send"></i>
                                    Send Message
                                </>
                            )}
                        </button>

                        {submitStatus === 'success' && (
                            <div className="form__message success">
                                <i className="bx bx-check-circle"></i>
                                Message sent successfully! I'll get back to you soon.
                            </div>
                        )}
                        {submitStatus === 'error' && (
                            <div className="form__message error">
                                <i className="bx bx-error-circle"></i>
                                <div>
                                    <div>Unable to send message. Please email me directly at:</div>
                                    <a href="mailto:mootazhalleb@gmail.com" style={{color: 'inherit', fontWeight: 'bold'}}>mootazhalleb@gmail.com</a>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
        </section>
    )
}

export default Contact
